import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import { formatRelativeTime } from '@/pages/activity/utils';
import type { AskUserRequest, InboxItem } from '@/types/inbox';

interface RequestStatusBannerProps {
  item: InboxItem;
  request: AskUserRequest;
}

export function RequestStatusBanner({ item, request }: RequestStatusBannerProps) {
  if (request.status === 'answered') {
    const who = request.answeredBy ?? item.resolvedBy;
    const when = request.answeredAt ?? item.resolvedAt;
    return (
      <Banner
        tone="success"
        icon={<CheckCircle2 className="h-4 w-4" />}
        text={
          <>
            Answered
            {who ? <> by <span className="font-medium">{who}</span></> : null}
            {when ? <> · {formatRelativeTime(when)}</> : null}
          </>
        }
      />
    );
  }

  if (request.status === 'cancelled') {
    const reason = item.cancelReason;
    const reasonLabel =
      reason === 'run_cancelled'
        ? 'run cancelled'
        : reason === 'agent_recalled'
        ? 'agent recalled'
        : reason === 'user_dismissed'
        ? 'dismissed'
        : undefined;
    return (
      <Banner
        tone="muted"
        icon={<XCircle className="h-4 w-4" />}
        text={
          <>
            Cancelled
            {reasonLabel ? <> · {reasonLabel}</> : null}
          </>
        }
      />
    );
  }

  if (request.status === 'expired') {
    return (
      <Banner
        tone="muted"
        icon={<Clock className="h-4 w-4" />}
        text="Expired"
      />
    );
  }

  return null;
}

function Banner({
  tone,
  icon,
  text,
}: {
  tone: 'success' | 'muted';
  icon: React.ReactNode;
  text: React.ReactNode;
}) {
  const toneClasses =
    tone === 'success'
      ? 'border-emerald-300/40 bg-emerald-50 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-950/30 dark:text-emerald-100'
      : 'border-border bg-muted text-muted-foreground';
  return (
    <div
      className={
        'flex items-center gap-2 rounded-md border px-3 py-2 text-sm ' + toneClasses
      }
    >
      <span className="shrink-0">{icon}</span>
      <span>{text}</span>
    </div>
  );
}
